import { DEFAULT_BREAKPOINT_NAME } from './constants';

// TODO: Refactor with GridBoundingBox type from utils.ts

/**
 * Tracks rectangles placed on the pegboard and the current row extent.
 * `maxRow` is kept one past the last occupied row when any rect extends beyond
 * the container's initial row count, providing a buffer for subsequent searches.
 */

/**
 * Finds the closest available position on the pegboard for a rect of the given
 * size, records it in the pegboard occupancy, and returns the placed position.
 *
 * The search scans top-to-bottom, left-to-right starting from (`row`, `column`).
 * If no position fits within the current grid bounds, falls back to the first
 * column of the row immediately after `maxRow` and reports `rowsNeededToFit` so
 * the caller can expand the container layout accordingly.
 */
export function findAndPlaceOnPegboard({
  pegboardOccupancy,
  pegboardContainerNode,
  row,
  column,
  rowsSpanned,
  columnsSpanned,
  targetBreakpoint = DEFAULT_BREAKPOINT_NAME
}) {
  const containerLayout = pegboardContainerNode.getContainerLayout(targetBreakpoint);
  const maxCol = containerLayout.columns.length;
  const maxRow = pegboardOccupancy.maxRow;
  for (const {
    row: destRow,
    column: destCol
  } of generateSearchPositions({
    row,
    column,
    rowsSpanned,
    columnsSpanned,
    maxRow,
    maxCol
  })) {
    const rowsNeededToFit = Math.max(0, destRow + rowsSpanned - 1 - maxRow);
    if (!isIntersecting(pegboardOccupancy.rects, destRow, destCol, rowsSpanned, columnsSpanned)) {
      addPlacedRect(pegboardOccupancy, destRow, destCol, rowsSpanned, columnsSpanned);
      return {
        row: destRow,
        column: destCol,
        rowsNeededToFit
      };
    }
  }
  return {
    row: maxRow + 1,
    column: 1,
    rowsNeededToFit: rowsSpanned
  };
}

/**
 * Yields candidate positions (row, column) for placing a rect of the given size,
 * scanning top-to-bottom, left-to-right starting from (`row`, `column`).
 * Rows are searched up to `maxRow + rowsSpanned - 1` so that a rect whose top
 * edge starts within the current grid can still extend one or more rows past it.
 */
function* generateSearchPositions({
  row,
  column,
  rowsSpanned,
  columnsSpanned,
  maxRow,
  maxCol
}) {
  for (let rowCandidate = row; rowCandidate <= maxRow + rowsSpanned - 1; rowCandidate++) {
    for (let colCandidate = column; colCandidate + columnsSpanned - 1 <= maxCol; colCandidate++) {
      yield {
        row: rowCandidate,
        column: colCandidate
      };
    }
  }
}

/**
 * Returns `true` if the candidate rect overlaps any rect in `rects` using an
 * axis-aligned bounding box test.
 */
function isIntersecting(rects, rowStart, colStart, rowsSpanned, columnsSpanned) {
  for (const rect of rects) {
    if (rowStart < rect.rowStart + rect.rowsSpanned && rect.rowStart < rowStart + rowsSpanned && colStart < rect.colStart + rect.columnsSpanned && rect.colStart < colStart + columnsSpanned) {
      return true;
    }
  }
  return false;
}

/**
 * Sorts pegboard children so that manually user-positioned nodes come first,
 * followed by auto-placed nodes ordered by their position on `referenceBreakpoint`
 * (row ascending, then column ascending).
 *
 * Manually positioned nodes are placed before auto-placed ones so that their
 * occupied cells are registered in the pegboard occupancy before autoplacement runs.
 */
export function getChildrenByXYOrder(tree, children, targetBreakpoint, referenceBreakpoint) {
  return children.sort((a, b) => {
    const isManuallyPositionedA = a.isManuallyPositionedByUserOnPegboard(targetBreakpoint);
    const isManuallyPositionedB = b.isManuallyPositionedByUserOnPegboard(targetBreakpoint);
    if (isManuallyPositionedA && !isManuallyPositionedB) {
      return -1;
    }
    if (!isManuallyPositionedA && isManuallyPositionedB) {
      return 1;
    }
    const {
      rowStart: rowStartA,
      columnStart: columnStartA
    } = tree.getPositionOfPegboardNode(a, referenceBreakpoint);
    const {
      rowStart: rowStartB,
      columnStart: columnStartB
    } = tree.getPositionOfPegboardNode(b, referenceBreakpoint);
    return rowStartA - rowStartB || columnStartA - columnStartB;
  });
}

/** Creates a `PegboardOccupancy` sized to the container's initial row count. */
export function buildEmptyPegboardOccupancy(rows) {
  return {
    rects: [],
    maxRow: rows
  };
}

/**
 * Records a placed rectangle in the pegboard occupancy and extends `maxRow` if the
 * rect reaches past the current bound. `maxRow` is set to `rowStart + rowsSpanned`
 * (one past the last occupied row) to leave a search buffer for subsequent placements.
 */
export function addPlacedRect(pegboardOccupancy, rowStart, colStart, rowsSpanned, columnsSpanned) {
  pegboardOccupancy.rects.push({
    rowStart,
    colStart,
    rowsSpanned,
    columnsSpanned
  });
  if (rowStart + rowsSpanned - 1 > pegboardOccupancy.maxRow) {
    pegboardOccupancy.maxRow = rowStart + rowsSpanned;
  }
}

/**
 * Resolves the row and column span to use when autoplacing `node` at
 * `targetBreakpoint`.
 *
 * Precedence for column span:
 * 1. `targetColumnsSpanned` if explicitly provided.
 * 2. The node's existing span if it fits within the container width.
 * 3. Full container width as a fallback.
 *
 * Throws if the node's parent is not a pegboard container.
 */
export function computeTargetBreakpointSpanningForAutoplacement({
  tree,
  node,
  targetRowsSpanned,
  targetColumnsSpanned,
  targetBreakpoint = DEFAULT_BREAKPOINT_NAME,
  isNewNode = false
}) {
  var _ref;
  const parentNode = node.getParentNode();
  if (!parentNode.isPegboardContainer()) {
    throw new Error('Parent node is not a pegboard container');
  }
  const containerLayout = parentNode.getContainerLayout(targetBreakpoint);
  const nodeSpanning = tree.getSpanningOfPegboardNode(node, targetBreakpoint, !isNewNode);
  const rowsSpannedToUse = (_ref = targetRowsSpanned !== null && targetRowsSpanned !== void 0 ? targetRowsSpanned : nodeSpanning.rowsSpanned) !== null && _ref !== void 0 ? _ref : 1;
  let columnsSpannedToUse;
  if (targetColumnsSpanned) {
    columnsSpannedToUse = targetColumnsSpanned;
  } else if (nodeSpanning.columnsSpanned && 1 + nodeSpanning.columnsSpanned <= containerLayout.columns.length) {
    columnsSpannedToUse = nodeSpanning.columnsSpanned;
  } else {
    // Fallback to the full width of the container.
    columnsSpannedToUse = containerLayout.columns.length || 1;
  }
  return {
    rowsSpanned: rowsSpannedToUse,
    columnsSpanned: columnsSpannedToUse
  };
}